import { useState } from "react";
import { Button } from "../../components/button";
import { Checkbox } from "../../components/checkbox";

export type EditableFieldValue = string | number | string[] | boolean;

interface EditableFieldProps {
  editing: boolean;
  value: EditableFieldValue;
  onChange: (newValue: EditableFieldValue) => void;
}

const EditableField = ({ editing, value, onChange }: EditableFieldProps) => {
  const [inputValue, setInputValue] = useState<EditableFieldValue>(value);

  const stopClickPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleSave = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    onChange(inputValue);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputValue(e.target.value);
  };

  const handleBooleanChange = (checked: boolean) => {
    setInputValue(checked);
    onChange(checked);
  };

  const enterSave = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  const renderEditField = () => {
    if (typeof value === "boolean") {
      return (
        <Checkbox
          checked={inputValue as boolean}
          onCheckedChange={handleBooleanChange}
        />
      );
    }

    if (Array.isArray(value)) {
      return (
        <textarea
          className="border border-gray-300 p-1 rounded"
          value={(inputValue as string[]).join(", ")}
          onClick={(e) => stopClickPropagation(e)}
          onChange={(e) => setInputValue(e.target.value.split(", "))}
        />
      );
    }

    return (
      <input
        autoFocus
        type="text"
        className="border w-full border-gray-300 p-1 rounded z-10 h-8"
        value={inputValue as string | number}
        onClick={(e) => stopClickPropagation(e)}
        onBlur={() => {
          if (inputValue !== value) {
            handleSave();
          }
        }}
        onChange={handleChange}
        onKeyDown={enterSave}
      />
    );
  };

  const renderValue = () => {
    if (typeof inputValue === "boolean") {
      return inputValue ? "True" : "False";
    }

    if (inputValue == null) {
      return <span className="text-gray-500 italic">No value</span>;
    }

    return Array.isArray(inputValue) ? inputValue.join(", ") : inputValue;
  };

  const wasEdited = inputValue !== value;

  return (
    <div className="flex flex-col items-start justify-between my-1 w-full pr-2">
      {editing ? (
        <div className="flex items-center w-full">
          {renderEditField()}
          {wasEdited && (
            <Button
              onClick={handleSave}
              className="ml-2"
              variant="link"
              size="sm"
            >
              Save
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between w-full cursor-pointer">
          <span className="text-gray-900">{renderValue()}</span>
        </div>
      )}
    </div>
  );
};

export { EditableField };
